import React, { memo, useEffect, useState } from 'react';

interface Props {
  showAfter?: number;
  duration?: number;
}

const ScrollToTopButtonComponent = ({ showAfter = 300, duration = 300 }: Props) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > showAfter);
    };

    // check once on mount
    onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [showAfter]);

  // Prefer native smooth behavior, fallback to JS animation if not supported
  const scrollToTop = () => {
    if ('scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // fallback: simple JS ease-out
    const start = window.scrollY;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      window.scrollTo(0, Math.round(start * (1 - eased)));
      if (t < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  return (
    <button
      onClick={scrollToTop}
      className={`opacity-60 focus:opacity-100 hover:opacity-100 fixed right-4 bottom-6 z-50 flex h-12 w-12 items-center justify-center rounded-full shadow-lg ring-1 ring-black/10 transition-transform duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary transform-gpu
        ${visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0 pointer-events-none'}`}
      style={{ background: 'linear-gradient(180deg,#008AC7,#006E98)' }}
    >
      {/* Up arrow SVG */}
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='0 0 24 24'
        fill='none'
        stroke='white'
        strokeWidth={2}
        strokeLinecap='round'
        strokeLinejoin='round'
        className='h-6 w-6'
        aria-hidden
      >
        <path d='M18 15l-6-6-6 6' />
      </svg>
    </button>
  );
};

export const ScrollToTopButton = memo(ScrollToTopButtonComponent);

/*
Usage:
1) Put this file in your React project (e.g. src/components/ScrollToTopButton.tsx)
2) Make sure Tailwind CSS is configured in your project
3) Import and render once near the root of your app (e.g. inside App.tsx):

   import ScrollToTopButton from "./components/ScrollToTopButton";
   function App(){
     return (
       <>
         <YourRoutesOrContent />
         <ScrollToTopButton showAfter={250} />
       </>
     )
   }

Notes:
- Button uses Tailwind utility classes; style prop adds a small gradient so you don't need extra CSS.
- The component is fully TypeScript-safe and runs on Linux/Windows/MacOS (no OS-specific code).
- Accessible: has aria-label and focus-visible ring.
*/
